import type { ScannerData } from '../types/ScannerData';
import { resolveSymbol } from '../services/symbolPricingService';

/** Index / weekly roots that share one options chain family in the UI. */
const CANONICAL_ALIASES: Record<string, string[]> = {
  SPX: ['SPX', 'SPXW', '.SPX', '$SPX', 'SP500'],
  NDX: ['NDX', 'NDXP', 'NDXQ'],
  RUT: ['RUT', 'RUTW'],
  VIX: ['VIX', 'VIXW'],
};

function normalizeToken(sym: string): string {
  return sym.trim().toUpperCase().replace(/^\$/, '').replace(/^\./, '');
}

/** Display / filter key for matching chain rows (SPXW → SPX, etc.). */
export function canonicalUnderlyingKey(sym: string): string {
  const u = normalizeToken(sym);
  if (!u) return '';
  for (const [canon, aliases] of Object.entries(CANONICAL_ALIASES)) {
    if (aliases.includes(u) || u.startsWith(canon)) return canon;
  }
  const resolved = resolveSymbol(u);
  return normalizeToken(resolved?.displaySymbol || resolved?.gexUnderlying || u);
}

export function rowUnderlyingSymbol(row: ScannerData): string {
  const u = String(
    row.Underlying ?? row.underlyingSymbol ?? (row as { underlying_symbol?: string }).underlying_symbol ?? '',
  ).trim();
  return u;
}

/** Match scanner row to a dashboard symbol (SPX matches SPXW rows). */
export function rowMatchesUnderlyingFilter(row: ScannerData, filterSymbol: string): boolean {
  const filt = (filterSymbol || '').trim();
  if (!filt || filt === '—' || filt === '-') return true;
  const rowU = rowUnderlyingSymbol(row);
  if (!rowU) return true;
  return canonicalUnderlyingKey(rowU) === canonicalUnderlyingKey(filt);
}

export function medianChainUnderlyingPrice(rows: ScannerData[], filterSymbol: string): number {
  const prices: number[] = [];
  for (const r of rows) {
    if (!rowMatchesUnderlyingFilter(r, filterSymbol)) continue;
    const p = Number(r['Underlying Price']);
    if (Number.isFinite(p) && p > 0) prices.push(p);
  }
  if (!prices.length) return 0;
  prices.sort((a, b) => a - b);
  const mid = Math.floor(prices.length / 2);
  return prices.length % 2 ? prices[mid] : (prices[mid - 1] + prices[mid]) / 2;
}

export function sampleStrikesForSymbol(rows: ScannerData[], filterSymbol: string): number[] {
  const out: number[] = [];
  for (const r of rows) {
    if (!rowMatchesUnderlyingFilter(r, filterSymbol)) continue;
    const k = Number(r.Strike);
    if (Number.isFinite(k) && k > 0) out.push(k);
  }
  return out;
}

/**
 * Keep live quote on the same scale as chain strikes (fixes SPX index vs ETF proxy mismatches).
 */
export function reconcileSpotWithChain(
  liveSpot: number,
  chainSpot: number,
  strikes: number[],
): number {
  if (chainSpot > 0 && liveSpot > 0) {
    const ratio = liveSpot / chainSpot;
    if (ratio > 1.35 || ratio < 0.74) return chainSpot;
    return liveSpot;
  }
  if (chainSpot > 0) return chainSpot;
  if (liveSpot > 0) return liveSpot;
  if (strikes.length) {
    const sorted = [...strikes].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  }
  return 0;
}
